/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'

interface NetworkStatus {
    isOffline: boolean
    hasServerError: boolean
    reportServerError: () => void
    reportSuccess: () => void
}

const NetworkStatusContext = createContext<NetworkStatus>({
    isOffline: false,
    hasServerError: false,
    reportServerError: () => undefined,
    reportSuccess: () => undefined,
})

export const useNetworkStatus = () => useContext(NetworkStatusContext)

const SERVER_ERROR_THRESHOLD = 3
const SERVER_ERROR_WINDOW_MS = 60_000

export const NetworkStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isOffline, setIsOffline] = useState(() => !navigator.onLine)
    const [hasServerError, setHasServerError] = useState(false)
    const errorTimestamps = useRef<number[]>([])

    useEffect(() => {
        const onOnline = () => {
            setIsOffline(false)
            setHasServerError(false)
            errorTimestamps.current = []
        }
        const onOffline = () => setIsOffline(true)
        window.addEventListener('online', onOnline)
        window.addEventListener('offline', onOffline)
        return () => {
            window.removeEventListener('online', onOnline)
            window.removeEventListener('offline', onOffline)
        }
    }, [])

    const reportServerError = useCallback(() => {
        if (!navigator.onLine) return
        const now = Date.now()
        errorTimestamps.current = [
            ...errorTimestamps.current.filter(t => now - t < SERVER_ERROR_WINDOW_MS),
            now,
        ]
        if (errorTimestamps.current.length >= SERVER_ERROR_THRESHOLD) {
            setHasServerError(true)
        }
    }, [])

    const reportSuccess = useCallback(() => {
        if (hasServerError) {
            setHasServerError(false)
            errorTimestamps.current = []
        }
    }, [hasServerError])

    useEffect(() => {
        const onServerError = () => reportServerError()
        const onServerOk = () => reportSuccess()
        window.addEventListener('dhtn:server-error', onServerError)
        window.addEventListener('dhtn:server-ok', onServerOk)
        return () => {
            window.removeEventListener('dhtn:server-error', onServerError)
            window.removeEventListener('dhtn:server-ok', onServerOk)
        }
    }, [reportServerError, reportSuccess])

    return (
        <NetworkStatusContext.Provider value={{ isOffline, hasServerError, reportServerError, reportSuccess }}>
            {children}
        </NetworkStatusContext.Provider>
    )
}
